import React, { useState, useEffect } from "react";
import { Line, Bar, Pie, Radar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import SubPageLayout from "../../components/shared/SubPageLayout";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  Filler,
);

// Mock data for charts and metrics
const mockCallData = {
  labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
  datasets: [
    {
      label: "Client Engagement Score",
      data: [7.5, 8.2, 7.8, 8.5, 8.9, 9.1],
      borderColor: "#202EC6",
      backgroundColor: "rgba(32, 46, 198, 0.1)",
      fill: true,
    },
  ],
};

const mockHealthScoreData = {
  labels: [
    "Call Time",
    "Marketing Efforts",
    "Administrative Balance",
    "Client Retention",
    "Portfolio Research",
  ],
  datasets: [
    {
      label: "Current Score",
      data: [85, 72, 68, 90, 75],
      backgroundColor: "rgba(32, 46, 198, 0.2)",
      borderColor: "#202EC6",
      borderWidth: 2,
    },
  ],
};

const mockLeaderboardData = {
  labels: ["Week 1", "Week 2", "Week 3", "Week 4"],
  datasets: [
    {
      label: "Your Performance",
      data: [82, 85, 88, 92],
      backgroundColor: "#202EC6",
    },
  ],
};

const SalesCoach = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [isRecording, setIsRecording] = useState(false);

  const breadcrumbItems = [
    { label: "Marketing & Sales", link: "/marketing-sales" },
    { label: "Sales Coach", link: "/marketing-sales/sales-coach" },
  ];

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "call-analytics", label: "Call Analytics" },
    { id: "training", label: "Training & Learning" },
    { id: "advisor-health", label: "Advisor Health" },
    { id: "gamification", label: "Gamification" },
  ];

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
      },
    },
    scales: {
      r: {
        beginAtZero: true,
        max: 100,
        ticks: {
          stepSize: 20,
        },
      },
    },
  };

  const renderOverviewTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-sm">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
            Recent Performance
          </h3>
          <div className="h-64">
            <Line data={mockCallData} options={chartOptions} />
          </div>
        </div>
        <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-sm">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
            Key Metrics
          </h3>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-500">Average Call Duration</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">32 mins</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-500">Client Engagement Score</p>
              <p className="text-2xl font-semibold text-[#202EC6]">8.5/10</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-500">Success Rate</p>
              <p className="text-2xl font-semibold text-green-600">78%</p>
            </div>
          </div>
        </div>
        <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-sm">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
            Quick Actions
          </h3>
          <div className="space-y-4">
            <button
              onClick={() => setActiveTab("call-analytics")}
              className="w-full py-2 px-4 bg-[#202EC6] text-white rounded hover:bg-opacity-90"
            >
              Start Coaching Now
            </button>
            <button className="w-full py-2 px-4 border border-gray-300 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800">
              Schedule Training
            </button>
            <button className="w-full py-2 px-4 border border-gray-300 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800">
              View Resources
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderCallAnalyticsTab = () => (
    <div className="space-y-6">
      <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-sm">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            Call Recording & Transcription
          </h3>
          <div className="flex items-center space-x-4">
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                isRecording
                  ? "bg-red-100 text-red-800"
                  : "bg-gray-100 text-gray-800 dark:text-gray-200"
              }`}
            >
              {isRecording ? "Recording" : "Ready"}
            </span>
            <button
              onClick={() => setIsRecording(!isRecording)}
              className={`px-4 py-2 rounded ${
                isRecording
                  ? "bg-red-600 text-white hover:bg-red-700"
                  : "bg-[#202EC6] text-white hover:bg-opacity-90"
              }`}
            >
              {isRecording ? "Stop Recording" : "Start Recording"}
            </button>
          </div>
        </div>
        <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
            Recent Call Summary
          </h4>
          <div className="bg-gray-50 p-4 rounded">
            <p className="text-gray-600 dark:text-gray-400">
              No recent calls recorded. Start a new recording to begin analyzing
              your sales performance.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-sm">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
            Performance Metrics
          </h3>
          <div className="h-64">
            <Bar data={mockCallData} options={chartOptions} />
          </div>
        </div>
        <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-sm">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
            AI Feedback
          </h3>
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2">
                Communication Style
              </h4>
              <p className="text-blue-800">
                Focus on active listening and asking more open-ended questions.
              </p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <h4 className="font-medium text-green-900 mb-2">
                Objection Handling
              </h4>
              <p className="text-green-800">
                Good use of social proof when addressing concerns about market
                volatility.
              </p>
            </div>
            <div className="p-4 bg-yellow-50 rounded-lg">
              <h4 className="font-medium text-yellow-900 mb-2">
                Closing Techniques
              </h4>
              <p className="text-yellow-800">
                Consider using more assumptive closes when discussing next
                steps.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTrainingTab = () => (
    <div className="space-y-6">
      <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
          Available Training Modules
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { title: "Mastering Client Discovery", progress: 75 },
            { title: "Advanced Objection Handling", progress: 45 },
            { title: "Building Trust Remotely", progress: 90 },
          ].map((module, index) => (
            <div key={index} className="border rounded-lg p-4">
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">{module.title}</h4>
              <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4">
                <div
                  className="bg-[#202EC6] h-2.5 rounded-full"
                  style={{ width: `${module.progress}%` }}
                ></div>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {module.progress}% Complete
              </p>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
          Upcoming Webinars
        </h3>
        <div className="space-y-4">
          {[
            {
              title: "Market Volatility Communication Strategies",
              date: "2024-03-15",
              time: "2:00 PM EST",
            },
            {
              title: "Building a Referral Network",
              date: "2024-03-22",
              time: "1:00 PM EST",
            },
          ].map((webinar, index) => (
            <div
              key={index}
              className="flex justify-between items-center p-4 border rounded-lg"
            >
              <div>
                <h4 className="font-medium text-gray-900 dark:text-gray-100">{webinar.title}</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {webinar.date} at {webinar.time}
                </p>
              </div>
              <button className="px-4 py-2 bg-[#202EC6] text-white rounded hover:bg-opacity-90">
                Register
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderAdvisorHealthTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-sm">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
            Advisor Health Score
          </h3>
          <div className="h-64">
            <Radar data={mockHealthScoreData} options={chartOptions} />
          </div>
        </div>
        <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-sm">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
            Optimization Suggestions
          </h3>
          <div className="space-y-4">
            {[
              {
                area: "Time Management",
                suggestion: "Schedule client calls in focused blocks",
              },
              {
                area: "Client Engagement",
                suggestion: "Increase follow-up frequency with top clients",
              },
              {
                area: "Professional Development",
                suggestion: "Complete advanced certification modules",
              },
            ].map((item, index) => (
              <div key={index} className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-1">{item.area}</h4>
                <p className="text-gray-600 dark:text-gray-400">{item.suggestion}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderGamificationTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-sm">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
            Your Performance
          </h3>
          <div className="h-64">
            <Bar data={mockLeaderboardData} options={chartOptions} />
          </div>
        </div>
        <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-sm">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
            Achievements
          </h3>
          <div className="grid grid-cols-2 gap-4">
            {[
              {
                name: "Client Champion",
                description: "Maintained 90%+ satisfaction rate",
                progress: 85,
              },
              {
                name: "Growth Master",
                description: "Exceeded quarterly targets",
                progress: 70,
              },
              {
                name: "Knowledge Seeker",
                description: "Completed 10 training modules",
                progress: 60,
              },
              {
                name: "Team Player",
                description: "Contributed to team success",
                progress: 95,
              },
            ].map((achievement, index) => (
              <div key={index} className="p-4 border rounded-lg">
                <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
                  {achievement.name}
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  {achievement.description}
                </p>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div
                    className="bg-[#202EC6] h-2.5 rounded-full"
                    style={{ width: `${achievement.progress}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case "overview":
        return renderOverviewTab();
      case "call-analytics":
        return renderCallAnalyticsTab();
      case "training":
        return renderTrainingTab();
      case "advisor-health":
        return renderAdvisorHealthTab();
      case "gamification":
        return renderGamificationTab();
      default:
        return renderOverviewTab();
    }
  };

  return (
    <SubPageLayout
      title="Sales Coach"
      subtitle="Enhance your sales skills with personalized insights and feedback"
      breadcrumbItems={breadcrumbItems}
    >
      <div className="space-y-6">
        {/* Tab Navigation */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                  ${
                    activeTab === tab.id
                      ? "border-[#202EC6] text-[#202EC6]"
                      : "border-transparent text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300"
                  }
                `}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        {renderTabContent()}
      </div>
    </SubPageLayout>
  );
};

export default SalesCoach;
